import { BET_STATE, TOGGLE, ACTION_VARIANT, AUTO_CASHOUT, PANELS } from '@constants/bettingConstants'
import { GAME_STATUS } from '@sockets/socketEvents'

/**
 * Pure freebet logic — the SINGLE home for three things that the old
 * implementations spread across component state, navigation hooks and mega
 * effects (and got wrong):
 *   1. which freebet a panel is currently SHOWING (a derived pointer, never a copy),
 *   2. the "no duplicate across panels" rule (one function, not four),
 *   3. re-pointing idle panels when the server list refetches.
 * Nothing here touches the store or React — it is a function of its arguments,
 * so the rendered card can never desync from the state machine.
 */

const { ENABLED, DISABLED } = TOGGLE
const bool = (on) => (on ? ENABLED : DISABLED)

// A freebet the player can still stake this round: it has remaining slots.
// (The synced list is already filtered to ACTIVE status by the query.)
export const isPlayable = (fb) => !!fb && (fb.remaining ?? 0) > 0

// A bet is "in flight" this round — the panel is COMMITTED to a freebet: its card
// is frozen on the placed snapshot and its selection can't move. This starts the
// moment the bet is queued (even though it is still frontend-only and cancelable),
// because that freebet is already earmarked and must not be re-pointed under the
// player. IDLE and terminal (cashed-out / crashed) panels behave alike — they show
// a fresh freebet from the list and can bet for the next round, like Classic.
const IN_FLIGHT = [
  BET_STATE.BET_PLACED,
  BET_STATE.BET_SUBMITTED,
  BET_STATE.BET_ACCEPTED,
  BET_STATE.BET_RUNNING,
]
export const isInFlight = (betState) => IN_FLIGHT.includes(betState)

// PLACE_BET has been sent (or already acked) — the bet is FINAL. Distinct from
// `isInFlight`, which also covers the still-cancelable pending window.
const COMMITTED = [BET_STATE.BET_SUBMITTED, BET_STATE.BET_ACCEPTED, BET_STATE.BET_RUNNING]
const isCommitted = (betState) => COMMITTED.includes(betState)

// The freebet a panel is currently showing: the frozen SNAPSHOT while a bet is
// in flight (so list churn mid-round never changes the card), otherwise the live
// SELECTION from the list (idle or after settle/crash).
export const renderedFreebetId = (panel) => {
  if (!panel) return null
  return isInFlight(panel.betState) ? (panel.placedFreebet?.id ?? null) : panel.selectedFreebetId
}

const otherPanelId = (panel) => (panel === PANELS.ONE ? PANELS.TWO : PANELS.ONE)

/**
 * The freebets a panel may cycle through with its arrows: every playable
 * freebet MINUS the one the other panel is currently showing (dual mode only).
 * This is the one and only place the cross-panel exclusion lives.
 */
export const availableForPanel = ({ list, panels, panel, dualMode }) => {
  const excludeId = dualMode ? renderedFreebetId(panels[otherPanelId(panel)]) : null
  return (list ?? []).filter((fb) => isPlayable(fb) && fb.id !== excludeId)
}

/**
 * Re-point each IDLE panel's selection at a still-available freebet — keeping a
 * valid current selection, never letting both panels show the same freebet, and
 * leaving null (→ "No Freebets available for slot") when nothing is free. Active
 * (non-idle) panels are LOCKED: their card renders the placed snapshot, so their
 * selection is untouched and their shown freebet is reserved from the other panel.
 * Returns a { [ONE], [TWO] } id map. Deterministic: panel ONE picks first.
 */
export const reconcileSelections = ({ list, panels, dualMode }) => {
  const rows = list ?? []
  const scope = dualMode ? [PANELS.ONE, PANELS.TWO] : [PANELS.ONE]
  const taken = new Set()
  const next = { [PANELS.ONE]: null, [PANELS.TWO]: null }

  // pass 1 — in-flight panels keep their placed freebet and reserve its id
  scope.forEach((id) => {
    const p = panels[id]
    if (isInFlight(p.betState)) {
      next[id] = p.selectedFreebetId
      const shown = renderedFreebetId(p)
      if (shown != null) taken.add(shown)
    }
  })

  // pass 2 — idle + terminal panels keep a still-valid selection, else take the
  // first free one (terminal re-points to a fresh freebet for the next round)
  scope.forEach((id) => {
    const p = panels[id]
    if (isInFlight(p.betState)) return
    const current = p.selectedFreebetId
    const currentOk =
      current != null && !taken.has(current) && isPlayable(rows.find((fb) => fb.id === current))
    const chosen = currentOk
      ? current
      : (rows.find((fb) => isPlayable(fb) && !taken.has(fb.id))?.id ?? null)
    if (chosen != null) taken.add(chosen)
    next[id] = chosen
  })

  return next
}

/**
 * Decide, per panel, what the player can interact with. Pure function of the
 * panel state + round + two booleans the hook already computes (whether a
 * freebet is shown, and whether there is more than one to swap to). Mirrors the
 * Classic `deriveInteractionControls` shape but with no stake/auto-bet controls.
 */
export const deriveFreebetControls = ({ panel, round, hasFreebet, canSwap, isExpired }) => {
  const { betState, pending, autoCashout } = panel
  const status = round?.status ?? null
  // Terminal (cashed-out / crashed) behaves like idle so the player can queue the
  // next round the instant a bet resolves — exactly like Classic's `idleish`.
  const isIdleish = !isInFlight(betState)
  const isPending = betState === BET_STATE.BET_PLACED

  // If auto-cashout is on, the target must be within [MIN, MAX] to place —
  // exactly like Classic. Off = no constraint.
  const autoCashoutOk =
    autoCashout.status !== TOGGLE.ENABLED ||
    (Number(autoCashout.value) >= AUTO_CASHOUT.MIN && Number(autoCashout.value) <= AUTO_CASHOUT.MAX)

  let variant
  if (betState === BET_STATE.BET_RUNNING) {
    variant = ACTION_VARIANT.CASHOUT
  } else if (betState === BET_STATE.BET_SUBMITTED || betState === BET_STATE.BET_ACCEPTED) {
    variant = ACTION_VARIANT.PLACED // sent to the server — locked in
  } else if (isPending) {
    variant = ACTION_VARIANT.CANCEL
  } else {
    variant = ACTION_VARIANT.BET // idle or terminal — ready for the next round
  }

  let enabled
  if (variant === ACTION_VARIANT.CASHOUT) {
    enabled = !pending // one cashout at a time
  } else if (variant === ACTION_VARIANT.PLACED) {
    enabled = false // final — the button is a badge, not an action
  } else if (variant === ACTION_VARIANT.CANCEL) {
    // A pending bet never left the client, so discarding it is pure frontend
    // state — always allowed, with nothing to undo server-side.
    enabled = isPending
  } else {
    // BET — idle OR just settled/crashed (re-bet for the next round), with a
    // freebet shown, a valid target, not in maintenance, and NOT time-expired.
    // No balance check — a freebet debits 0.
    enabled =
      isIdleish &&
      hasFreebet &&
      autoCashoutOk &&
      !isExpired &&
      status !== GAME_STATUS.UNDER_MAINTENANCE &&
      status !== null
  }

  // Arrows stay usable when expired (so the player can move off the dead freebet)
  // but not while a bet is pending — the card is frozen on the queued freebet, so
  // swapping means Cancel first, which frees the panel instantly.
  // Auto-cashout stays editable right through the pending window (it is read at
  // send time), and is blocked only once submitted, or while expired.
  const canSwapNow = isIdleish && !pending
  const canEditTarget = !isCommitted(betState) && !pending
  return {
    prevArrow: bool(canSwapNow && canSwap),
    nextArrow: bool(canSwapNow && canSwap),
    autoCashout: bool(canEditTarget && hasFreebet && !isExpired),
    actionButton: bool(enabled), // ENABLED | DISABLED
    actionVariant: variant, // BET | CANCEL | PLACED | CASHOUT
  }
}
